import {
  GRADE_OPTIONS,
  nameBirthdayKey,
  normalizePersonName,
  parseBirthday,
  type School,
} from "./data";
import { formatPhoneInput, phoneDigits } from "./phone";
import {
  consentFlags,
  emptyPermission,
  GENDERS,
  HELP_INTERESTS,
  normalizePermission,
  permissionIsFiled,
  RELATIONSHIPS,
  type GenderId,
  type HelpInterestId,
  type PermissionAnswers,
  type RelationshipId,
  type YesNo,
  type AgreeChoice,
  type RaceId,
} from "./permissionForm";

export type AhqFiledStudent = {
  /** 1-based line in the export, including the two header rows. */
  line: number;
  given: string;
  family: string;
  email: string;
  birthday: string;
  school: School | "";
  grade: number | "";
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  emergencyName: string;
  emergencyPhone: string;
  permission: PermissionAnswers;
};

type Column = { section: string; label: string; index: number };

/** AfterSchoolHQ permission export: a section row, then a question row, then one row per signup. */
export function isAhqPermissionExport(table: string[][]): boolean {
  const labels = table[1] ?? [];
  const sections = table[0] ?? [];
  const hasDob = labels.some((label) => /date of birth/i.test(label));
  const hasMedical = labels.some((label) => /medical conditions/i.test(label));
  const hasSection = sections.some((label) => /participant information/i.test(label));
  return hasDob && hasMedical && hasSection;
}

/**
 * One student per name and birthday. Yearly programming rows win over a single
 * activity signup, then the latest submission.
 */
export function studentsFromAhqExport(table: string[][]): AhqFiledStudent[] {
  if (!isAhqPermissionExport(table)) return [];
  const columns = headedColumns(table);
  const at = (section: RegExp, label: RegExp) =>
    columns.find((column) => section.test(column.section) && label.test(column.label))?.index ??
    -1;

  const indexes = {
    submitted: at(/^$/, /submission date/i),
    activity: at(/^$/, /^activities$/i),
    given: at(/participant information/i, /^first name$/i),
    family: at(/participant information/i, /^last name$/i),
    email: at(/participant information/i, /^email$/i),
    grade: at(/participant information/i, /^grade$/i),
    birthday: at(/participant information/i, /date of birth/i),
    studentPhone: at(/participant information/i, /student phone/i),
    address: at(/participant information/i, /^address$/i),
    gender: at(/participant information/i, /^gender$/i),
    race: at(/participant information/i, /501\(c\)\(3\)|funded through private/i),
    attends: at(/participant information/i, /my student attends/i),
    pickup: at(/participant information/i, /may leave programming with/i),
    walk: at(/participant information/i, /walk home/i),
    schoolName: at(/participant information/i, /^school name$/i),
    guardianGiven: at(/^parent\/guardian information$/i, /^first name$/i),
    guardianFamily: at(/^parent\/guardian information$/i, /^last name$/i),
    guardianEmail: at(/^parent\/guardian information$/i, /^email$/i),
    guardianPhone: at(/^parent\/guardian information$/i, /^phone$/i),
    guardianRelationship: at(/^parent\/guardian information$/i, /relationship/i),
    guardianAddress: at(/^parent\/guardian information$/i, /address/i),
    help: at(/^parent\/guardian information$/i, /interested in helping/i),
    extraGiven: at(/additional parent/i, /^first name$/i),
    extraMiddle: at(/additional parent/i, /^middle name$/i),
    extraFamily: at(/additional parent/i, /^last name$/i),
    extraEmail: at(/additional parent/i, /^email$/i),
    extraPhone: at(/additional parent/i, /^phone$/i),
    extraRelationship: at(/additional parent/i, /relationship/i),
    extraAddress: at(/additional parent/i, /^address$/i),
    emergencyGiven: at(/^emergency contact 1$/i, /^first name$/i),
    emergencyFamily: at(/^emergency contact 1$/i, /^last name$/i),
    emergencyPhone: at(/^emergency contact 1$/i, /^phone$/i),
    emergencyRelationship: at(/^emergency contact 1$/i, /relationship/i),
    emergency2Given: at(/^emergency contact 2$/i, /^first name$/i),
    emergency2Family: at(/^emergency contact 2$/i, /^last name$/i),
    emergency2Phone: at(/^emergency contact 2$/i, /^phone$/i),
    emergency2Relationship: at(/^emergency contact 2$/i, /relationship/i),
    behavioral: at(/health information/i, /safe and supportive|behavioral or emotional/i),
    medical: at(/health information/i, /medical conditions/i),
    otc: at(/health information/i, /over-the-counter|administer prescription/i),
    emergencyCare: at(/health information/i, /time-sensitive emergency|immediate medica/i),
    physician: at(/health information/i, /physician name/i),
    physicianPhone: at(/health information/i, /physician phone/i),
    hospital: at(/health information/i, /preferred hospital/i),
    insurance: at(/health information/i, /insurance provider/i),
    policy: at(/health information/i, /policy number/i),
    conduct: at(/policies & agreements/i, /lead with respect/i),
    behavior: at(/policies & agreements/i, /strike 1/i),
    dismissal: at(/policies & agreements/i, /only leave/i),
    media: at(/policies & agreements/i, /media release/i),
    participate: at(/liability/i, /off-site recreational/i),
    transport: at(/liability/i, /recreational venues/i),
    risks: at(/liability/i, /physical injury/i),
    release: at(/liability/i, /this release includes/i),
  };
  const healthDetails = columns
    .filter(
      (column) =>
        /health information/i.test(column.section) && /if you answered yes/i.test(column.label),
    )
    .map((column) => column.index);

  const chosen = new Map<string, { row: string[]; line: number; rank: number }>();
  table.forEach((row, index) => {
    if (index < 2) return;
    const given = cell(row, indexes.given);
    const family = cell(row, indexes.family);
    if (!given || !family) return;
    const birthday = parseBirthday(cell(row, indexes.birthday)) ?? "";
    const key = birthday
      ? nameBirthdayKey(given, family, birthday)
      : `${normalizePersonName(given)}|${normalizePersonName(family)}|`;
    const programming = /programming\s*\(/i.test(cell(row, indexes.activity));
    const submitted = Date.parse(cell(row, indexes.submitted));
    const rank = (programming ? 1_000_000_000_000 : 0) + (Number.isNaN(submitted) ? 0 : submitted);
    const current = chosen.get(key);
    if (!current || rank >= current.rank) chosen.set(key, { row, line: index + 1, rank });
  });

  return [...chosen.values()].map(({ row, line }) => {
    const given = cell(row, indexes.given);
    const family = cell(row, indexes.family);
    const birthday = parseBirthday(cell(row, indexes.birthday)) ?? "";
    const school =
      schoolFrom(
        cell(row, indexes.attends),
        cell(row, indexes.schoolName),
        cell(row, indexes.activity),
      );
    const grade = gradeFrom(cell(row, indexes.grade));
    const guardianGiven = cell(row, indexes.guardianGiven);
    const guardianFamily = cell(row, indexes.guardianFamily);
    const emergencyGiven = cell(row, indexes.emergencyGiven);
    const emergencyFamily = cell(row, indexes.emergencyFamily);
    const extraGiven = [cell(row, indexes.extraGiven), cell(row, indexes.extraMiddle)]
      .filter(Boolean)
      .join(" ");
    const permission = emptyPermission();
    permission.student = {
      firstName: given,
      lastName: family,
      email: cell(row, indexes.email),
      grade,
      birthday,
      phone: phone(cell(row, indexes.studentPhone)),
      address: cell(row, indexes.address),
      gender: genderFrom(cell(row, indexes.gender)),
      races: racesFrom(cell(row, indexes.race)),
      school,
      pickupPeople: cell(row, indexes.pickup),
      walkHome: yesNo(cell(row, indexes.walk)),
    };
    permission.guardian = {
      firstName: guardianGiven,
      lastName: guardianFamily,
      email: cell(row, indexes.guardianEmail),
      phone: phone(cell(row, indexes.guardianPhone)),
      relationship: relationshipFrom(cell(row, indexes.guardianRelationship)),
      address: cell(row, indexes.guardianAddress),
      help: helpFrom(cell(row, indexes.help)),
    };
    permission.additionalGuardian = {
      firstName: extraGiven,
      lastName: cell(row, indexes.extraFamily),
      email: cell(row, indexes.extraEmail),
      phone: phone(cell(row, indexes.extraPhone)),
      relationship: relationshipFrom(cell(row, indexes.extraRelationship)),
      address: cell(row, indexes.extraAddress),
    };
    permission.emergency1 = {
      firstName: emergencyGiven,
      lastName: emergencyFamily,
      phone: phone(cell(row, indexes.emergencyPhone)),
      relationship: cell(row, indexes.emergencyRelationship),
    };
    permission.emergency2 = {
      firstName: cell(row, indexes.emergency2Given),
      lastName: cell(row, indexes.emergency2Family),
      phone: phone(cell(row, indexes.emergency2Phone)),
      relationship: cell(row, indexes.emergency2Relationship),
    };
    permission.health = {
      behavioral: yesNo(cell(row, indexes.behavioral)),
      behavioralDetail: cell(row, healthDetails[0] ?? -1),
      medical: yesNo(cell(row, indexes.medical)),
      medicalDetail: cell(row, healthDetails[1] ?? -1),
      otcMeds: yesNo(cell(row, indexes.otc)),
      emergencyCare: yesNo(cell(row, indexes.emergencyCare)),
      physicianName: cell(row, indexes.physician),
      physicianPhone: phone(cell(row, indexes.physicianPhone)),
      hospital: cell(row, indexes.hospital),
      insuranceProvider: cell(row, indexes.insurance),
      policyNumber: cell(row, indexes.policy),
    };
    permission.policies = {
      conduct: agree(cell(row, indexes.conduct)),
      behavior: agree(cell(row, indexes.behavior)),
      dismissal: agree(cell(row, indexes.dismissal)),
      media: yesNo(cell(row, indexes.media)),
    };
    permission.liability = {
      participate: signed(cell(row, indexes.participate)),
      transport: signed(cell(row, indexes.transport)),
      risks: signed(cell(row, indexes.risks)),
      release: signed(cell(row, indexes.release)),
    };
    return {
      line,
      given,
      family,
      email: cell(row, indexes.email),
      birthday,
      school,
      grade,
      guardianName: `${guardianGiven} ${guardianFamily}`.trim(),
      guardianEmail: cell(row, indexes.guardianEmail),
      guardianPhone: phone(cell(row, indexes.guardianPhone)),
      emergencyName: `${emergencyGiven} ${emergencyFamily}`.trim(),
      emergencyPhone: phone(cell(row, indexes.emergencyPhone)),
      permission,
    };
  });
}

export type PermissionBackfillStudent = {
  id: string;
  given: string;
  family: string;
  birthday: string;
};

export type PermissionBackfillSubmission = {
  id: string;
  formId: string;
  studentId: string;
  answers: unknown;
};

export type PermissionBackfillUpdate = {
  id: string;
  answers: Record<string, unknown>;
};

export type PermissionBackfillPlan = {
  updates: PermissionBackfillUpdate[];
  matched: number;
  alreadyFiled: number;
  missingSubmission: number;
  unmatched: string[];
};

/**
 * Copy an AfterSchoolHQ form onto a checklist-only submission.
 * A student who already has the full form is left alone.
 */
export function planPermissionBackfill(
  filed: readonly AhqFiledStudent[],
  roster: readonly PermissionBackfillStudent[],
  submissions: readonly PermissionBackfillSubmission[],
  openFormId: string,
): PermissionBackfillPlan {
  const byKey = new Map(
    filed.map((student) => [
      nameBirthdayKey(student.given, student.family, student.birthday),
      student,
    ]),
  );
  const updates: PermissionBackfillUpdate[] = [];
  let matched = 0;
  let alreadyFiled = 0;
  let missingSubmission = 0;
  const unmatched: string[] = [];

  roster.forEach((student) => {
    const birthday = student.birthday.slice(0, 10);
    const key = nameBirthdayKey(student.given, student.family, birthday);
    const source = byKey.get(key);
    if (!source) {
      unmatched.push(`${student.given} ${student.family} (${birthday || "no birthday"})`);
      return;
    }
    matched += 1;
    const submission = submissions.find(
      (row) => row.studentId === student.id && row.formId === openFormId,
    );
    if (!submission || !isAnswerRecord(submission.answers)) {
      missingSubmission += 1;
      return;
    }
    if (permissionIsFiled(normalizePermission(submission.answers.permission))) {
      alreadyFiled += 1;
      return;
    }
    const flags = consentFlags(source.permission);
    updates.push({
      id: submission.id,
      answers: {
        ...submission.answers,
        permissionSlips: flags.permissionSlips,
        medical: flags.medical,
        conduct: flags.conduct,
        permission: source.permission,
      },
    });
  });

  return { updates, matched, alreadyFiled, missingSubmission, unmatched };
}

function isAnswerRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function headedColumns(table: string[][]): Column[] {
  const sections = table[0] ?? [];
  const labels = table[1] ?? [];
  let carried = "";
  return labels.map((label, index) => {
    const own = (sections[index] ?? "").trim();
    if (own) carried = own;
    return { section: own || carried, label: label.trim(), index };
  });
}

function cell(row: string[], index: number): string {
  if (index < 0) return "";
  return (row[index] ?? "").trim();
}

function phone(value: string): string {
  const digits = phoneDigits(value);
  if (digits.length === 10) return formatPhoneInput(digits);
  return value.trim();
}

function yesNo(value: string): YesNo | "" {
  const key = value.trim().toLowerCase();
  if (key.startsWith("yes")) return "yes";
  if (key.startsWith("no")) return "no";
  return "";
}

function agree(value: string): AgreeChoice | "" {
  const key = value.trim().toLowerCase();
  if (!key) return "";
  if (key.includes("meeting") || key.includes("don't agree") || key.includes("do not agree")) {
    return "meeting";
  }
  if (key.startsWith("yes") || key.includes("i agree")) return "agree";
  return "";
}

function signed(value: string): boolean {
  return value.trim().length > 0;
}

function compact(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function genderFrom(value: string): GenderId | "" {
  const key = value.trim().toLowerCase();
  if (!key) return "";
  if (key.startsWith("female")) return "female";
  if (key.startsWith("male")) return "male";
  const match = GENDERS.find((item) => key.includes(item.en.toLowerCase()));
  return match?.id ?? "";
}

function racesFrom(value: string): RaceId[] {
  const text = compact(value);
  if (!text) return [];
  const ids: RaceId[] = [];
  if (text.includes("africanamerican")) ids.push("african-american");
  if (text.includes("caucasian")) ids.push("caucasian");
  else if (text.includes("asian")) ids.push("asian");
  if (text.includes("hispanic") || text.includes("latino")) ids.push("hispanic-latino");
  if (text.includes("nativeamerican")) ids.push("native-american");
  return ids;
}

function relationshipFrom(value: string): RelationshipId | "" {
  const key = compact(value);
  return RELATIONSHIPS.find((item) => compact(item.en) === key)?.id ?? "";
}

function helpFrom(value: string): HelpInterestId[] {
  const text = compact(value);
  if (!text) return [];
  return HELP_INTERESTS.filter((item) => text.includes(compact(item.en))).map((item) => item.id);
}

function schoolFrom(...values: string[]): School | "" {
  const text = values.join(" ").toLowerCase();
  if (text.includes("glen lake")) return "Glen Lake";
  if (text.includes("suttons")) return "Suttons Bay";
  if (text.includes("northport")) return "Northport";
  if (text.includes("leland")) return "Leland";
  return "";
}

function gradeFrom(value: string): number | "" {
  const trimmed = value.trim();
  const byLabel = GRADE_OPTIONS.find((item) => item.label.toLowerCase() === trimmed.toLowerCase());
  if (byLabel) return byLabel.value;
  const match = trimmed.match(/-?\d+/);
  if (!match) return "";
  const grade = Number(match[0]);
  return GRADE_OPTIONS.some((item) => item.value === grade) ? grade : "";
}
